import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, X, SlidersHorizontal, MapPin } from "lucide-react";
import { useTranslation } from 'react-i18next';

// ─── Section options ──────────────────────────────────────────
const getSections = (t) => [
  { value: "all", label: t('section.allSections') || "All Sections" },
  { value: "R.R.T", label: "R.R.T" },
  { value: "GRANT", label: t('section.grant') || "GRANT" },
];

// ─── Hoblis with circles ─────────────────────────────────────
const HOBLIS_WITH_CIRCLES = ['TIPPASANDRA', 'KASABA'];

const getCircles = (t, hobliFilter) => {
  if (!hobliFilter) return [{ value: "all", label: t('circle.allCircles') || "All Circles" }];
  const hobliUpper = hobliFilter.toUpperCase();
  if (hobliUpper === 'TIPPASANDRA') {
    return [
      { value: "all", label: t('circle.allCircles') || "All Circles" },
      { value: "tippasandra", label: t('circle.tippasandra') || "Tippasandra" },
      { value: "kodiyala", label: t('circle.kodiyala') || "Kodiyala" },
      { value: "solur", label: t('circle.solur') || "Solur" },
    ];
  }
  if (hobliUpper === 'KASABA') {
    return [
      { value: "all", label: t('circle.allCircles') || "All Circles" },
      { value: "kasaba", label: t('circle.kasaba') || "Kasaba" },
      { value: "kalya", label: t('circle.kalya') || "Kalya" },
    ];
  }
  return [{ value: "all", label: t('circle.allCircles') || "All Circles" }];
};

// ─── Status options ──────────────────────────────────────────
const STATUS_OPTIONS = [
  "submitted", "assigned", "document_verification", "field_verification",
  "under_review", "revenue_approved", "survey_inspection",
  "boundary_verification", "sketch_preparation", "order_issued",
];

// ─── Component ───────────────────────────────────────────────
export default function DashboardFilters({
  searchInputRef,
  onSearch,
  onClearSearch,
  searchQuery,
  statusFilter,
  onStatusChange,
  sectionFilter,
  onSectionChange,
  circleFilter,
  onCircleChange,
  districtFilter,
  onDistrictChange,
  talukFilter,
  onTalukChange,
  hobliFilter,
  onHobliChange,
  villageFilter,
  onVillageChange,
  showFilters,        // kept for API compat but unused
  onToggleFilters,    // kept for API compat but unused
  availableDistricts = [],
  availableTaluks = [],
  availableHoblis = [],
  availableVillages = [],
  onClearAllFilters,
  onTableLoading,
  user,
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'kn' ? 'kn' : 'en';
  const sections = getSections(t);
  const circles = getCircles(t, hobliFilter);

  const SURVEY_DESIGNATIONS = ['ddlr', 'adlr', 'surveyor', 'case_worker', 'superintendent', 'survey_supervisor'];
  const isSurveyUser = user?.department === 'survey' || SURVEY_DESIGNATIONS.includes(user?.designation);
  const showCircleFilter = !isSurveyUser;
  const circlesAvailable = HOBLIS_WITH_CIRCLES.includes(hobliFilter?.toUpperCase());

  const getDisplayName = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return currentLang === 'kn' ? loc.name_kn : loc.name_en;
  };
  const getKey = (loc) => {
    if (!loc) return '';
    if (typeof loc === 'string') return loc;
    return loc.key;
  };

  // ── Modal state ──
  const [open, setOpen] = useState(false);

  // Pending (draft) filter values inside the modal — cloned from live filters on open
  const [draft, setDraft] = useState({});

  const openModal = () => {
    setDraft({
      status: statusFilter,
      section: sectionFilter,
      circle: circleFilter,
      district: districtFilter,
      taluk: talukFilter,
      hobli: hobliFilter,
      village: villageFilter,
    });
    setOpen(true);
  };

  const applyFilters = () => {
    // Close modal first, then trigger loading + filter changes
    setOpen(false);
    
    // Activate spinner immediately
    onTableLoading?.(true);
    
    // Apply all filter changes in a microtask to batch them after modal closes
    setTimeout(() => {
      if (draft.status !== statusFilter) onStatusChange(draft.status);
      if (draft.section !== sectionFilter) onSectionChange(draft.section);
      if (draft.circle !== circleFilter) onCircleChange(draft.circle);
      if (draft.district !== districtFilter) onDistrictChange(draft.district);
      if (draft.taluk !== talukFilter) onTalukChange(draft.taluk);
      if (draft.hobli !== hobliFilter) onHobliChange(draft.hobli);
      if (draft.village !== villageFilter) onVillageChange(draft.village);
    }, 50);
  };

  const resetDraft = () => {
    setDraft({
      status: "submitted",
      section: "all",
      circle: "all",
      district: "all",
      taluk: "all",
      hobli: "all",
      village: "all",
    });
  };

  // ── Active filter count ──
  const activeCount = (() => {
    let n = 0;
    if (statusFilter !== "submitted") n++;
    if (sectionFilter !== "all") n++;
    if (showCircleFilter && circleFilter !== "all") n++;
    if (districtFilter !== "all" && districtFilter !== "RAMANAGARA" && districtFilter !== "Ramanagara") n++;
    if (talukFilter !== "all" && talukFilter !== "MAGADI" && talukFilter !== "Magadi") n++;
    if (hobliFilter !== "all") n++;
    if (villageFilter !== "all") n++;
    if (searchQuery.trim() !== "") n++;
    return n;
  })();

  // Draft circles depend on draft hobli
  const draftCircles = getCircles(t, draft.hobli);
  const draftCirclesAvailable = HOBLIS_WITH_CIRCLES.includes(draft.hobli?.toUpperCase());

  // ── Render: Compact bar + tags + modal ──
  return (
    <>
      {/* ── Compact Bar ── */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {/* Search input */}
        <div className="relative flex-1 min-w-[220px] max-w-[480px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('search.placeholder') || 'Search by Application ID, Farmer Name, Survey Number or Village...'}
            defaultValue=""
            onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            className="w-full h-10 pl-10 pr-4 text-sm border border-stone-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            data-testid="search-input"
          />
        </div>
        <Button
          onClick={onSearch}
          className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="search-button"
        >
          <Search className="w-4 h-4 mr-1.5" />
          {t('common.search') || 'Search'}
        </Button>
        {searchQuery.trim() && (
          <Button
            onClick={onClearSearch}
            variant="outline"
            size="sm"
            className="h-10 px-3 text-slate-500"
            data-testid="clear-search-button"
          >
            <X className="w-4 h-4" />
          </Button>
        )}

        {/* Divider */}
        <div className="h-8 w-px bg-stone-200 mx-1 hidden sm:block" />

        {/* Filter & Search button */}
        <Button
          variant="outline"
          onClick={openModal}
          className="h-10 px-4 border-2 border-blue-500 text-blue-700 hover:bg-blue-50 font-medium"
          data-testid="open-filter-modal-btn"
        >
          <SlidersHorizontal className="w-4 h-4 mr-1.5" />
          {t('filter.filterAndSearch') || 'Filter & Search'}
          {activeCount > 0 && (
            <Badge className="ml-2 bg-emerald-600 text-white text-[10px] px-1.5 py-0">{activeCount}</Badge>
          )}
        </Button>

        {activeCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onClearAllFilters(); onClearSearch(); }}
            className="h-10 text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
            data-testid="clear-filters-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            {t('filter.clearAll') || 'Clear All'}
          </Button>
        )}
      </div>

      {/* ── Active Filter Tags ── */}
      {activeCount > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="active-filter-tags">
          {statusFilter !== "submitted" && (
            <FilterTag label={t(`status.${statusFilter}`)} color="blue" onRemove={() => { onTableLoading?.(true); onStatusChange("submitted"); }} />
          )}
          {sectionFilter !== "all" && (
            <FilterTag label={t(`section.${sectionFilter}`)} color="emerald" onRemove={() => { onTableLoading?.(true); onSectionChange("all"); }} />
          )}
          {(districtFilter !== "all" && districtFilter !== "RAMANAGARA" && districtFilter !== "Ramanagara") && (
            <FilterTag label={districtFilter} color="indigo" onRemove={() => { onTableLoading?.(true); onDistrictChange("all"); }} />
          )}
          {(talukFilter !== "all" && talukFilter !== "MAGADI" && talukFilter !== "Magadi") && (
            <FilterTag label={talukFilter} color="cyan" onRemove={() => { onTableLoading?.(true); onTalukChange("all"); }} />
          )}
          {hobliFilter !== "all" && (
            <FilterTag label={hobliFilter} color="teal" onRemove={() => { onTableLoading?.(true); onHobliChange("all"); }} />
          )}
          {showCircleFilter && circleFilter !== "all" && (
            <FilterTag label={t(`circle.${circleFilter}`)} color="amber" onRemove={() => { onTableLoading?.(true); onCircleChange("all"); }} />
          )}
          {villageFilter !== "all" && (
            <FilterTag label={villageFilter} color="rose" onRemove={() => { onTableLoading?.(true); onVillageChange("all"); }} />
          )}
          {searchQuery.trim() !== "" && (
            <FilterTag label={`"${searchQuery}"`} color="purple" onRemove={onClearSearch} />
          )}
        </div>
      )}

      {/* ── Filter Modal ── */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-y-auto" data-testid="filter-modal">
          <DialogHeader>
            <DialogTitle className="text-emerald-700 flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5" />
              {t('filter.filterAndSearch') || 'Filter & Search'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* ── 1. Application Status ── */}
            <FilterSection title={t('filter.searchByStatus') || 'Search Application By Status'} icon="status">
              <select
                value={draft.status}
                onChange={(e) => setDraft(d => ({ ...d, status: e.target.value }))}
                className="h-10 w-full px-3 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="modal-status-filter"
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{t(`status.${s}`)}</option>
                ))}
              </select>
            </FilterSection>

            {/* ── 2. Location Filters ── */}
            <FilterSection title={t('filter.locationFilters') || 'Location Filters'} icon="location">
              <div className="grid grid-cols-2 gap-3">
                {/* District */}
                <FilterDropdown
                  label={t('location.district') || 'District'}
                  value={draft.district}
                  onChange={(v) => setDraft(d => ({ ...d, district: v }))}
                  testId="modal-district-filter"
                >
                  <option value="all">{t('location.allDistricts') || 'All Districts'}</option>
                  {availableDistricts.map((d) => (
                    <option key={getKey(d)} value={getKey(d)} disabled={d.is_active === false}>
                      {getDisplayName(d)}{d.is_active === false ? ` (${t('common.comingSoon')})` : ''}
                    </option>
                  ))}
                </FilterDropdown>

                {/* Taluk */}
                <FilterDropdown
                  label={t('location.taluk') || 'Taluk'}
                  value={draft.taluk}
                  onChange={(v) => setDraft(d => ({ ...d, taluk: v }))}
                  testId="modal-taluk-filter"
                >
                  <option value="all">{t('location.allTaluks') || 'All Taluks'}</option>
                  {availableTaluks.map((tk) => (
                    <option key={getKey(tk)} value={getKey(tk)} disabled={tk.is_active === false}>
                      {getDisplayName(tk)}{tk.is_active === false ? ` (${t('common.comingSoon')})` : ''}
                    </option>
                  ))}
                </FilterDropdown>

                {/* Hobli */}
                <FilterDropdown
                  label={t('location.hobli') || 'Hobli'}
                  value={draft.hobli}
                  onChange={(v) => setDraft(d => ({ ...d, hobli: v, circle: "all" }))}
                  testId="modal-hobli-filter"
                >
                  <option value="all">{t('location.allHoblis') || 'All Hoblis'}</option>
                  {availableHoblis.map((h) => (
                    <option key={getKey(h)} value={getKey(h)}>{getDisplayName(h)}</option>
                  ))}
                </FilterDropdown>

                {/* Circle (Revenue only) */}
                {showCircleFilter && (
                  <FilterDropdown
                    label={t('circle.title') || 'Circle'}
                    value={draftCirclesAvailable ? draft.circle : "all"}
                    onChange={(v) => setDraft(d => ({ ...d, circle: v }))}
                    disabled={!draftCirclesAvailable}
                    testId="modal-circle-filter"
                  >
                    {draftCirclesAvailable ? (
                      draftCircles.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))
                    ) : (
                      <option value="all">{t('circle.selectHobliWithCircles') || 'Select Hobli with Circles'}</option>
                    )}
                  </FilterDropdown>
                )}

                {/* Village */}
                <FilterDropdown
                  label={`${t('location.village') || 'Village'} (${availableVillages.length})`}
                  value={draft.village}
                  onChange={(v) => setDraft(d => ({ ...d, village: v }))}
                  testId="modal-village-filter"
                >
                  <option value="all">{t('location.allVillages') || 'All Villages'} ({availableVillages.length})</option>
                  {availableVillages.map((v) => (
                    <option key={getKey(v)} value={getKey(v)}>{getDisplayName(v)}</option>
                  ))}
                </FilterDropdown>
              </div>
            </FilterSection>

            {/* ── 3. Department Section ── */}
            <FilterSection title={t('filter.departmentSection') || 'Department Section'} icon="section">
              <select
                value={draft.section}
                onChange={(e) => setDraft(d => ({ ...d, section: e.target.value }))}
                className="h-10 w-full sm:w-[240px] px-3 border border-stone-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="modal-section-filter"
              >
                {sections.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </FilterSection>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={resetDraft} className="text-red-500 border-red-200 hover:bg-red-50" data-testid="modal-reset-btn">
              <X className="w-4 h-4 mr-1" />
              {t('filter.clearAll') || 'Reset All'}
            </Button>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="modal-cancel-btn">
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button onClick={applyFilters} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="modal-apply-btn">
              <Search className="w-4 h-4 mr-1" />
              {t('filter.applyFilters') || 'Apply Filters'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Sub-components ──────────────────────────────────────────

const COLOR_MAP = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  purple: "bg-purple-50 text-purple-700 border-purple-200",
};

function FilterTag({ label, color = "blue", onRemove }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${COLOR_MAP[color] || COLOR_MAP.blue}`}>
      {label}
      <button onClick={onRemove} className="hover:opacity-70 ml-0.5">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function FilterSection({ title, icon, children }) {
  return (
    <div className="bg-stone-50 rounded-lg p-4">
      <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">{title}</h4>
      {children}
    </div>
  );
}

function FilterDropdown({ label, value, onChange, children, disabled, testId }) {
  return (
    <div className="flex flex-col">
      <label className="text-xs text-slate-500 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`h-9 px-3 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
          disabled ? 'bg-stone-100 text-slate-400 cursor-not-allowed' : 'bg-white'
        }`}
        data-testid={testId}
      >
        {children}
      </select>
    </div>
  );
}
